import apiClient from '../utils/axiosConfig';

// Normaliza el status de Mission al formato que usan las pantallas
const missionStatus = (s) => {
  if (!s) return 'pending';
  const map = { PENDING: 'pending', IN_PROGRESS: 'in_progress', COMPLETED: 'completed', CANCELLED: 'cancelled' };
  return map[s] || s.toLowerCase();
};

export const normalizeMission = (m) => {
  const drReq = m.mission_request || m.drone_application_request || null;

  // Para mission_request, el status se deriva del MissionRequest
  // Para misiones normales, viene de Mission.status
  const isMissionRequest = !!drReq;
  const isMultispectral = drReq && drReq.type === 'multispectral';

  const itemStatus = drReq
    ? (drReq.cancelled ? 'cancelled'
        : m.status === 'COMPLETED' ? 'completed'
        : m.status === 'IN_PROGRESS' ? 'in_progress'
        : m.pilot ? 'assigned'
        : 'pending')
    : missionStatus(m.status);

  return {
    id:              `mission-${m.id}`,
    _type:           drReq ? 'mission_request' : 'mission',
    _raw_id:         drReq ? drReq.id : m.id,
    _mission_id:     m.id,
    status:          itemStatus,
    location_name:   m.location_name || (drReq ? drReq.location_name : null) || null,
    location_id:     drReq ? drReq.location_id : (m.location || null),
    location_polygon: drReq ? drReq.location_polygon : null,
    enterprise_name: m.enterprise_name || null,
    // Datos de misión regular
    mission_name:        m.name || `Misión #${m.sid || m.id}`,
    mission_description: m.description || '',
    mission_sid:         m.sid,
    aircraft_name:       m.aircraft_name || null,
    dgac_status:         m.dgac_permit_status || null,
    objectives:          (m.objectives || []).map(o => o.name || o),
    scheduled_date:      m.scheduled_date || (drReq ? drReq.scheduled_date : null),
    application_date:    drReq ? drReq.application_date : null,
    // Datos de mission_request (aplicacion o multiespectral)
    mr_type:         drReq ? drReq.type : null,
    order_id:        drReq ? drReq.order_id : null,
    order_folio:     drReq ? drReq.order_folio : null,
    field_blocks:    drReq ? (drReq.field_blocks || []) : [],
    products:        drReq ? (drReq.products || []) : [],
    epp:             drReq ? (drReq.epp || {}) : {},
    wetting_per_ha:  drReq ? drReq.wetting_per_ha : null,
    rate_type:       drReq ? drReq.rate_type : null,
    dose_per_ha:     drReq ? drReq.dose_per_ha : null,
    dose_unit:       drReq ? drReq.dose_unit : null,
    prescription_map:     drReq ? drReq.prescription_map : null,
    prescription_classes: drReq ? drReq.prescription_classes : null,
    vra_source_scene_id:  drReq ? drReq.vra_source_scene_id : null,
    execution_records: drReq ? (drReq.execution_records || []) : [],
    cancelled:       drReq ? drReq.cancelled : false,
    // Campos multiespectral
    objectives_list: drReq ? (drReq.objectives_list || []) : [],
    flight_altitude_agl: drReq ? drReq.flight_altitude_agl : null,
    aircraft:        (drReq ? drReq.aircraft : null) || m.aircraft_details || null,
    pilot:           (drReq ? drReq.pilot : null) || null,
    created_at:      m.created_at,
  };
};

const pilotService = {
  getAllPilotMissions: async () => {
    const response = await apiClient.get('/missions/by-pilot/');
    const missions = response.data || [];
    return missions
      .map(normalizeMission)
      .sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at) : 0;
        const db = b.created_at ? new Date(b.created_at) : 0;
        return db - da;
      });
  },

  startMission: async (droneRequestId) => {
    const response = await apiClient.post(`/mission-requests/${droneRequestId}/start/`, {});
    return response.data;
  },

  recordBlock: async (droneRequestId, data) => {
    const response = await apiClient.post(`/mission-requests/${droneRequestId}/record-block/`, data);
    return response.data;
  },

  completeDroneMission: async (droneRequestId) => {
    const response = await apiClient.post(`/mission-requests/${droneRequestId}/complete/`, {});
    return response.data;
  },

  getMyAircraft: async () => {
    const response = await apiClient.get('/pilot-aircraft/');
    return response.data;
  },

  getMissionWeather: async (missionId) => {
    try {
      const response = await apiClient.get(`/missions/${missionId}/weather/`);
      return response.data;
    } catch (_) {
      return null;
    }
  },
};

// Calcula centroide de un item (field_blocks o location_polygon)
// y abre el navegador nativo
export const openNavigationToMission = (item) => {
  const { Linking, Platform } = require('react-native');

  let lat = null, lng = null;

  // Intentar desde field_blocks primero
  const blocks = (item.field_blocks || []).filter(b => b.polygon);
  if (blocks.length) {
    let latSum = 0, lngSum = 0, count = 0;
    blocks.forEach(b => {
      try {
        const geo = typeof b.polygon === 'string' ? JSON.parse(b.polygon) : b.polygon;
        geo.coordinates[0].forEach(([lng_, lat_]) => { latSum += lat_; lngSum += lng_; count++; });
      } catch (_) {}
    });
    if (count) { lat = latSum / count; lng = lngSum / count; }
  }

  // Fallback: location_polygon
  if ((lat === null) && item.location_polygon) {
    try {
      const geo = typeof item.location_polygon === 'string'
        ? JSON.parse(item.location_polygon) : item.location_polygon;
      let latSum = 0, lngSum = 0, count = 0;
      geo.coordinates[0].forEach(([lng_, lat_]) => { latSum += lat_; lngSum += lng_; count++; });
      if (count) { lat = latSum / count; lng = lngSum / count; }
    } catch (_) {}
  }

  if (lat === null) return false;

  const label = encodeURIComponent(item.location_name || 'Campo');
  if (Platform.OS === 'ios') {
    Linking.openURL(`maps://?q=${label}&ll=${lat},${lng}`);
  } else if (Platform.OS === 'android') {
    Linking.openURL(`geo:${lat},${lng}?q=${lat},${lng}(${label})`);
  } else {
    Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
  }
  return true;
};

export default pilotService;
